import type { DotProps } from "@/v2-backoffice-client/components/dot"
import type { ColumnFilterOption } from "@/v2-backoffice-client/components/table"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"

// The GraphQL ContactSyncStatus enum, as it reaches the client (string
// values). Kept as string literals so call sites don't have to import the
// generated enum object at runtime — same pattern as userIdentity.constants.
export type ContactSyncStatusValue = UserFragment["ContactSyncStatus"]

export const CONTACT_SYNC_STATUS_IN_SYNC = "IN_SYNC"
export const CONTACT_SYNC_STATUS_EMAIL_MISMATCH = "EMAIL_MISMATCH"
export const CONTACT_SYNC_STATUS_NOT_LINKED = "NOT_LINKED"
export const CONTACT_SYNC_STATUS_NOT_APPLICABLE = "NOT_APPLICABLE"

// One label + dot color per status — the single source for the badge,
// the healthy display, table grouping/export values, and filter options.
const STATUS_DISPLAY: Record<
  string,
  { label: string; variant: DotProps["variant"] }
> = {
  [CONTACT_SYNC_STATUS_NOT_LINKED]: {
    label: "Not in HubSpot",
    variant: "warning",
  },
  [CONTACT_SYNC_STATUS_EMAIL_MISMATCH]: {
    label: "Email differs",
    variant: "orange",
  },
  [CONTACT_SYNC_STATUS_IN_SYNC]: { label: "In sync", variant: "success-1" },
  [CONTACT_SYNC_STATUS_NOT_APPLICABLE]: {
    label: "Not applicable",
    variant: "muted",
  },
}

// Grouping / export / client-side-filter value for a status. Undefined
// tolerant for optional relations (TenantUserTable's User).
export function getContactSyncStatusLabel(
  status: ContactSyncStatusValue | undefined,
): string {
  if (!status) return ""
  return STATUS_DISPLAY[status as unknown as string]?.label ?? String(status)
}

// Options for the server-filtered Users board: values are the enum
// values the ContactSyncStatus filter registry entry compares against.
export const CONTACT_SYNC_STATUS_FILTER_OPTIONS: ColumnFilterOption[] =
  Object.entries(STATUS_DISPLAY).map(([value, display]) => ({
    value,
    label: display.label,
    dotVariant: display.variant,
  }))

// Options for client-side-filtered tables (customer / tenant users):
// their multi-select filter compares against the column's getValue —
// the label — not the enum value.
export const CONTACT_SYNC_STATUS_CLIENT_FILTER_OPTIONS: ColumnFilterOption[] =
  Object.values(STATUS_DISPLAY).map((display) => ({
    value: display.label,
    label: display.label,
    dotVariant: display.variant,
  }))

export type ContactStatusConfig = {
  label: string
  variant: DotProps["variant"]
  description: string
  // The repair action an operator should reach for.
  fix: string
}

// Only the actionable (out-of-sync) states carry a config; IN_SYNC /
// NOT_APPLICABLE are handled separately so the table stays clean.
const OUT_OF_SYNC_CONFIG: Record<string, ContactStatusConfig> = {
  [CONTACT_SYNC_STATUS_NOT_LINKED]: {
    ...STATUS_DISPLAY[CONTACT_SYNC_STATUS_NOT_LINKED],
    description:
      "No HubSpot contact is linked to this user, so the CRM has no record of them and no Placepoint usage data is pushed.",
    fix: "Fix with “Sync to HubSpot” — links the contact matching this email, or creates one.",
  },
  [CONTACT_SYNC_STATUS_EMAIL_MISMATCH]: {
    ...STATUS_DISPLAY[CONTACT_SYNC_STATUS_EMAIL_MISMATCH],
    description:
      "The linked HubSpot contact's email differs from the user's email here — one side changed after linking.",
    fix: "The contact email is CRM-owned: align it in HubSpot, or change the user's email if the app side is stale.",
  },
}

export function getContactStatusConfig(
  // undefined-tolerant: TenantUserTable reads it off an optional User.
  status: ContactSyncStatusValue | undefined,
): ContactStatusConfig | undefined {
  if (!status) return undefined
  return OUT_OF_SYNC_CONFIG[status as unknown as string]
}

// Whether the operator "Sync to HubSpot" action applies: only unlinked
// users are syncable from here (an email mismatch is resolved in HubSpot,
// not by re-pushing properties).
export function getContactSyncFix(
  status: ContactSyncStatusValue,
): "sync" | undefined {
  if ((status as unknown as string) === CONTACT_SYNC_STATUS_NOT_LINKED) {
    return "sync"
  }
  return undefined
}

// The "all good" / "no link expected" presentation (the detail panel
// and — with showHealthy — the table cells).
export function getContactHealthyDisplay(status: ContactSyncStatusValue): {
  variant: DotProps["variant"]
  label: string
} {
  if ((status as unknown as string) === CONTACT_SYNC_STATUS_IN_SYNC) {
    return STATUS_DISPLAY[CONTACT_SYNC_STATUS_IN_SYNC]
  }
  // NOT_APPLICABLE: deleted user or no email — nothing to link on.
  return STATUS_DISPLAY[CONTACT_SYNC_STATUS_NOT_APPLICABLE]
}

// HubSpot stores lifecycle stages as lowercase concatenated internal values
// ("salesqualifiedlead"). Map the standard stages to their portal labels;
// custom stages fall back to the raw value.
const LIFECYCLE_STAGE_LABELS: Record<string, string> = {
  subscriber: "Subscriber",
  lead: "Lead",
  marketingqualifiedlead: "Marketing Qualified Lead",
  salesqualifiedlead: "Sales Qualified Lead",
  opportunity: "Opportunity",
  customer: "Customer",
  evangelist: "Evangelist",
  other: "Other",
}

export function getUserContactLifecycleLabel(stage: string) {
  return LIFECYCLE_STAGE_LABELS[stage] ?? stage
}

// Lead statuses are SCREAMING_SNAKE internal values ("IN_PROGRESS",
// "ATTEMPTED_TO_CONTACT") — humanize instead of hardcoding the list, since
// portals commonly customize it.
export function getUserContactLeadStatusLabel(status: string) {
  const words = status.replace(/_/g, " ").toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}
